import { ObjectPath, SchemaFormErrorData } from "components/Form/components/SchemaForm/SchemaForm.types";
import { cloneDeep, get, isEmpty, isEqual, toPath } from "lodash";
import { capitalizeFirstLetter } from "utils/utils";

/** Stable empty path reused for schema/data roots. */
export const EMPTY_OBJECT_PATH: ObjectPath = [];

const objectPathCache = new Map<string, ObjectPath>();

/**
 * Returns a cached ObjectPath for `parent` extended by `segments`.
 * Reuses the same array reference for the same path so memoized children
 * can bail out when their path props are unchanged.
 */
export function extendObjectPath(parent: ObjectPath, ...segments: string[]): ObjectPath {
    if (segments.length === 0) {
        return parent;
    }

    const cacheKey =
        parent.length === 0 ? segments.join("\0") : `${parent.join("\0")}\0${segments.join("\0")}`;
    let cached = objectPathCache.get(cacheKey);
    if (!cached) {
        cached = parent.length === 0 ? [...segments] : [...parent, ...segments];
        objectPathCache.set(cacheKey, cached);
    }
    return cached;
}

const isNumericPathSegment = (segment: string) => /^\d+$/.test(segment);

const emptyChildContainer = (nextSegment: string | undefined) =>
    nextSegment !== undefined && isNumericPathSegment(nextSegment) ? [] : {};

/**
 * Immutably updates `data` at `path` with `value`, cloning only nodes along
 * the path so unrelated branches keep their original references.
 */
function withUpdatedPath(data: unknown, path: ObjectPath, value: unknown): unknown {
    if (path.length === 0) {
        return value;
    }

    const [head, ...rest] = path;

    if (Array.isArray(data)) {
        const index = Number(head);
        const child = data[index];
        if (rest.length === 0) {
            if (value === undefined) {
                if (!(index in data)) {
                    return data;
                }
                const copy = [...data];
                copy[index] = undefined;
                return copy;
            }
            if (child === value) {
                return data;
            }
            const copy = [...data];
            copy[index] = value;
            return copy;
        }

        const updatedChild = withUpdatedPath(
            child !== undefined && child !== null && typeof child === "object"
                ? child
                : emptyChildContainer(rest[0]),
            rest,
            value
        );
        if (updatedChild === child) {
            return data;
        }
        const copy = [...data];
        copy[index] = updatedChild;
        return copy;
    }

    if (data != null && typeof data === "object") {
        const record = data as Record<string, unknown>;
        const child = record[head];
        if (rest.length === 0) {
            if (value === undefined) {
                if (!(head in record)) {
                    return data;
                }
                const copy = { ...record };
                delete copy[head];
                return copy;
            }
            if (child === value) {
                return data;
            }
            return { ...record, [head]: value };
        }

        const updatedChild = withUpdatedPath(
            child !== undefined && child !== null && typeof child === "object"
                ? child
                : emptyChildContainer(rest[0]),
            rest,
            value
        );
        if (updatedChild === child) {
            return data;
        }
        return { ...record, [head]: updatedChild };
    }

    const container = isNumericPathSegment(head) ? [] : {};
    return withUpdatedPath(container, path, value);
}

/**
 * For a given data object, gets the value at the specified path.
 */
export function getByPath({
    data,
    dataPath,
    schema,
    elementSchema,
    defaultOverridePath,
}: {
    data: any;
    dataPath: ObjectPath;
    schema: Object;
    elementSchema: Object;
    defaultOverridePath?: ObjectPath;
}): any | null {
    if (data === undefined) {
        return getDefaultRequiredValue(schema, elementSchema, defaultOverridePath);
    }

    if (isEmpty(dataPath)) {
        return data;
    }

    if (typeof data === "object") {
        const value = get(data, dataPath);
        const hasAdditionalPropertiesPath = dataPath[dataPath.length - 1] === ADDITIONAL_PROPERTIES;
        const hasAdditionalProperty = hasAdditionalPropertiesPath && get(elementSchema, dataPath);
        if (
            value === true &&
            hasAdditionalPropertiesPath &&
            (hasAdditionalProperty || elementSchema["$id"])
        ) {
            return jsonFieldSchema;
        } else {
            return value;
        }
    }

    console.warn("getPath on non-object");
    return getDefaultRequiredValue(schema, elementSchema, defaultOverridePath);
}

export const jsonFieldSchema = { element: "form-json", default: null };

/**
 * Gets an updated data object.
 * @param data The current data object (eg. "values" of a form).
 * @param path The path to identify the field in the data object.
 * @param value The value to be stored.
 * @returns A copy of the data object containing the given value at the specified path.
 */
export function getUpdatedData(data, path, value): object {
    // Backward-compat: the immutable `withUpdatedPath` helper below operates on an
    // ObjectPath (array of segments) and destructures `[head, ...rest]`. Several
    // callers — notably the advanced-search FilterBuilder and the context-menu
    // "add filter" — still pass lodash-style STRING paths like "and[0].field".
    // Passing such a string straight to `withUpdatedPath` would destructure it into
    // individual characters and write garbage keys, silently dropping the update
    // (the root cause of the broken filter builder). Normalize strings to an
    // ObjectPath here; genuine ObjectPath arrays are left untouched (fast path).
    const normalizedPath: ObjectPath = typeof path === "string" ? toPath(path) : path;

    if (!normalizedPath.length) {
        return value;
    }

    if (data != null && (typeof data === "object" || Array.isArray(data))) {
        return withUpdatedPath(data, normalizedPath, value) as object;
    }

    const newData = withUpdatedPath({}, normalizedPath, value);
    console.warn(
        "setPath on non-object",
        "data",
        data,
        "path",
        path,
        "value",
        value,
        "newData",
        newData
    );
    return newData as object;
}

/**
 * Gets the default value for required field
 */

export const getDefaultRequiredValue = (schema, elementSchema, defaultOverridePath): any | null => {
    // TODO: check use of undefined. I would like to allow null as a valid value for forms.
    // But react complains when the value of an input is set to "null" and later changed (controlled state)
    if (!elementSchema) {
        return undefined;
    }

    if (defaultOverridePath != null) {
        return get(schema, defaultOverridePath, undefined);
    }

    if (typeof elementSchema !== "object") {
        console.warn("getDefault on non-object", elementSchema);
        return undefined;
    }

    if (
        (elementSchema.element === "form-dropdown" ||
            elementSchema.element === "form-optional-dropdown") &&
        elementSchema.hasOwnProperty("defaultIndex")
    ) {
        // A dropdown may list its options as `oneOf`/`anyOf` branches or as a
        // plain `enum` (inference renders an enum as const branches). A schema
        // with neither, or an index past the end, has no default to resolve.
        const branches = elementSchema.oneOf ?? elementSchema.anyOf;
        if (Array.isArray(branches)) {
            const branch = branches[elementSchema.defaultIndex];
            return branch === undefined
                ? undefined
                : getDefaultRequiredValue(schema, branch, defaultOverridePath);
        }
        if (Array.isArray(elementSchema.enum)) {
            return elementSchema.enum[elementSchema.defaultIndex];
        }
        return undefined;
    }

    if (
        (elementSchema.element === "form-array" ||
            elementSchema.element === "form-optional-array") &&
        !elementSchema.hasOwnProperty("default")
    ) {
        return [];
    }

    if (
        (elementSchema.element === "form-object" ||
            elementSchema.element === "form-optional-object") &&
        elementSchema.hasOwnProperty("properties")
    ) {
        const ret = Object.fromEntries(
            Object.entries(elementSchema.properties)
                .filter(([key]) => elementSchema.required?.includes(key))
                .map(([key, value]) => [
                    key,
                    getDefaultRequiredValue(schema, value, defaultOverridePath),
                ])
        );
        if (elementSchema.hasOwnProperty("additionalDefault")) {
            Object.assign(ret, cloneDeep(elementSchema.additionalDefault));
        }
        return ret;
    }

    if (
        (elementSchema.element === "form-object" ||
            elementSchema.element === "form-optional-object") &&
        (elementSchema.hasOwnProperty("patternProperties") ||
            elementSchema.hasOwnProperty("additionalProperties"))
    ) {
        if (elementSchema.hasOwnProperty("additionalDefault")) {
            return cloneDeep(elementSchema.additionalDefault);
        }
        return {};
    }

    if (elementSchema.element === "ref") {
        return getDefaultRequiredValue(schema, schema, defaultOverridePath);
    }

    if (elementSchema.element === "form-recursion") {
        return getDefaultRequiredValue(schema, schema, defaultOverridePath);
    }

    if (elementSchema.element === "form-const-null") {
        return null;
    }

    if (elementSchema.hasOwnProperty("default")) {
        return cloneDeep(elementSchema.default);
    }

    if (elementSchema.hasOwnProperty("const")) {
        return cloneDeep(elementSchema.const);
    }

    return undefined;
};

export const getIsOptional = (element: string) => element?.includes("optional");

/**
 * Whether a field's label shows the required asterisk. A key listed in the
 * schema's `required` array must be PRESENT, but a `form-optional-*` element
 * accepts `null` as its value, so the user may leave it empty: such a field is
 * optional from the user's point of view and carries no asterisk (e.g. a
 * nullable custom-object record-reference attribute). Required-ness for
 * validation and for keeping an unset field from being grayed out is unaffected.
 */
export const getShowsRequiredMark = (element: string | undefined, isStyleRequired?: boolean) =>
    Boolean(isStyleRequired) && !getIsOptional(element ?? "");

export const getSchemaFormErrorMessage = (dataPath: ObjectPath, errorData?: SchemaFormErrorData) =>
    isEqual(errorData?.path, dataPath) ? errorData?.message : "";

/**
 * True when the current validation error lies at, or anywhere beneath, `prefix`
 * (i.e. `prefix` is a prefix of `errorData.path`). Used to decide whether a
 * collapsed "More..." section contains the field that is blocking Save, so the
 * section can auto-expand and surface the otherwise-hidden error
 * (operator-task-fe-save-disabled-no-reason-when-hidden-attribute-invalid).
 */
export const isErrorPathWithin = (prefix: ObjectPath, errorData?: SchemaFormErrorData): boolean => {
    const errorPath = errorData?.path;
    return (
        Array.isArray(errorPath) &&
        errorPath.length >= prefix.length &&
        prefix.every((segment, index) => errorPath[index] === segment)
    );
};

export const fieldContainerClassName = (errorMessage?: string) =>
    `fieldContainer ${errorMessage ? "errorFieldContainer" : ""}`;

export const ADDITIONAL_PROPERTIES = "additionalProperties";

export const getAdditionalPropertyLabel = (
    labelReplacement: string,
    elementSchemaLabel?: string,
    startsWithCap?: boolean
) => {
    const label = elementSchemaLabel?.length ? elementSchemaLabel : labelReplacement;
    return startsWithCap ? capitalizeFirstLetter(label) : label;
};
